import React, { useState } from 'react';
import { 
  Sparkles, 
  Calendar,
  DollarSign,
  Package,
  Users,
  ArrowRight,
  Eye,
  Zap
} from 'lucide-react';
import { trackOnboarding } from '../../utils/analytics';

/**
 * Boply Logo Component
 */
const BoplyLogo = ({ size = 64 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <div 
      className="absolute bg-purple-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: 0, 
        left: 0,
        opacity: 0.5 
      }}
    />
    <div 
      className="absolute bg-purple-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: size * 0.22, 
        left: size * 0.22,
        opacity: 0.75 
      }}
    />
    <div 
      className="absolute bg-blue-500 rounded-[4px]" 
      style={{ 
        width: size * 0.55, 
        height: size * 0.55, 
        top: size * 0.45, 
        left: size * 0.45 
      }}
    />
  </div>
);

/**
 * WelcomeModal - First screen new users see
 * 
 * Two clear options:
 * 1. I'm Ready — Add My First Show (primary) - Jump straight to adding a show
 * 2. See How It Works (secondary) - Explore demo with sample data
 * 
 * The first option feels like the FASTER path, not the scarier one.
 */

const WelcomeModal = ({ onStartDemo, onStartFresh, onClose }) => {
  const [isHoveringDemo, setIsHoveringDemo] = useState(false);

  // When user clicks "See How It Works" (Demo)
  const handleStartDemo = () => {
    trackOnboarding.welcomeModalChoice('demo');
    trackOnboarding.demoStarted();
    onStartDemo();
  };

  // When user clicks "I'm Ready — Add My First Show"
  const handleStartFresh = () => {
    trackOnboarding.welcomeModalChoice('start_fresh');
    trackOnboarding.startFreshChosen();
    onStartFresh();
  };

  // If modal is dismissed without choosing
  const handleDismiss = () => {
    trackOnboarding.welcomeModalChoice('dismissed');
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-3 md:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl md:rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in max-h-[95vh] overflow-y-auto">
        
        {/* Header - Compact on mobile */}
        <div className="relative bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 px-5 pt-6 pb-8 md:px-8 md:pt-10 md:pb-12 text-center overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 left-4 w-32 h-32 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-4 right-4 w-40 h-40 bg-white rounded-full blur-3xl" />
          </div>
          
          {/* Logo - Using Boply logo */}
          <div className="relative inline-flex items-center justify-center mb-3 md:mb-5">
            <div className="md:hidden">
              <BoplyLogo size={56} />
            </div>
            <div className="hidden md:block">
              <BoplyLogo size={80} />
            </div>
            <Sparkles className="absolute -top-1 -right-1 md:-top-2 md:-right-2 text-yellow-300" size={20} />
          </div>
          
          <h1 className="relative text-2xl md:text-3xl font-bold text-white mb-1 md:mb-2">
            Welcome to Boply!
          </h1>
          <p className="relative text-white/80 text-sm md:text-base">
            Your all-in-one tool for managing gigs, money, and merch
          </p>
        </div>

        {/* Content - Compact spacing on mobile */}
        <div className="p-4 md:p-6 space-y-3 md:space-y-4">
          
          {/* PRIMARY CTA - I'm Ready (this should feel like the fast path) */}
          <button
            onClick={handleStartFresh}
            className="w-full group relative bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl md:rounded-2xl p-4 md:p-5 text-left transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/25 hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-start gap-3 md:gap-4">
              <div className="p-2 md:p-3 bg-white/20 rounded-lg md:rounded-xl group-hover:scale-110 transition-transform">
                <Zap size={22} className="md:hidden" />
                <Zap size={28} className="hidden md:block" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 md:mb-1 flex-wrap">
                  <span className="font-bold text-base md:text-lg">I'm Ready — Add My First Show</span>
                </div>
                <p className="text-white/80 text-xs md:text-sm leading-relaxed">
                  Takes 30 seconds. Just venue + date to get started.
                </p>
              </div>
              <ArrowRight size={18} className="mt-2 md:mt-3 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </div>
            
            {/* Quick stats preview */}
            <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-white/20 flex items-center justify-center gap-2 text-white/70 text-xs">
              <span>✓ 2 fields only</span>
              <span>•</span>
              <span>✓ Add details anytime</span>
            </div>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 md:gap-4 py-0.5 md:py-1">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            <span className="text-xs md:text-sm text-gray-400 dark:text-gray-500 font-medium">or</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          </div>

          {/* SECONDARY CTA - See How It Works (Demo) */}
          <button
            onClick={handleStartDemo}
            onMouseEnter={() => setIsHoveringDemo(true)}
            onMouseLeave={() => setIsHoveringDemo(false)}
            className="w-full group bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-xl md:rounded-2xl p-4 md:p-5 text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
          >
            <div className="flex items-center gap-3 md:gap-4">
              <div className="p-2 md:p-3 bg-gray-200 dark:bg-gray-700 rounded-lg md:rounded-xl group-hover:scale-110 transition-transform">
                <Eye size={22} className="text-gray-600 dark:text-gray-300 md:hidden" />
                <Eye size={28} className="text-gray-600 dark:text-gray-300 hidden md:block" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-bold text-base md:text-lg block mb-0.5 md:mb-1">See How It Works</span>
                <p className="text-gray-500 dark:text-gray-400 text-xs md:text-sm">
                  Explore with sample shows, merch & finances (30 sec)
                </p>
              </div>
              <ArrowRight size={18} className="text-gray-400 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </div>
            
            {/* Preview hint on hover - Smaller on mobile */}
            <div className={`mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-4 gap-1 md:gap-2 transition-all duration-300 ${isHoveringDemo ? 'opacity-100' : 'opacity-50'}`}>
              {[
                { icon: Calendar, label: '4 Shows' },
                { icon: DollarSign, label: '$2,150+' },
                { icon: Package, label: '5 Items' },
                { icon: Users, label: '3 Members' },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <item.icon size={14} className="mx-auto mb-0.5 opacity-70 md:hidden" />
                  <item.icon size={18} className="mx-auto mb-1 opacity-70 hidden md:block" />
                  <span className="text-[10px] md:text-xs opacity-70">{item.label}</span>
                </div>
              ))}
            </div>
          </button>

          {/* Trust indicators - Smaller on mobile */}
          <div className="pt-2 md:pt-4 flex items-center justify-center gap-3 md:gap-6 text-[10px] md:text-xs text-gray-400 dark:text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full" />
              Free to use
            </span>
            <span>•</span>
            <span>No credit card</span>
            <span>•</span>
            <span>Setup in 30 sec</span>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default WelcomeModal;